import 'chartjs-plugin-datalabels'; // Plugin for adding number overlays on the bar/pie slices

import {
  FluidChartInterface,
  FluidStackedChartTranslationOptions,
  FluidTheme,
  FluidThemeInterface,
  FluidVerticalPosition,
  isNullOrUndefined,
} from '@lmig/fluid-core';
import { generateUniqueId } from '@lmig/fluid-core/lib/helpers/string.helpers';
import {
  Component,
  ComponentInterface,
  Element,
  h,
  Prop,
  State,
  Watch,
} from '@stencil/core';
import { Chart, ChartSize } from 'chart.js'; // Charting library, uses HTML canvas

import {
  ChartLegendMarker,
  ChartSubtitle,
  ChartTitle,
} from '../../common/fluid-chart.components';
import { DARK_BLUE, WHITE } from '../../constants/fluid-chart.constants';
import { useI18n } from '../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import {
  ChartUtils,
  FluidChartStyles,
} from '../../utils/charts/fluid-chart-utils';
import { Utils } from '../../utils/utils';

/**
 * @displayName Stacked Horizontal Bar Chart
 *
 * @contextData FLUID Stacked Horizontal Bar Chart is a data visualization component used to compare values across groups of data. Each bar in the chart is divided into segments, with each segment representing a portion of the group's data. This segmentation allows for an easy comparison of the proportions within each group.
 */
@Component({
  tag: 'fluid-stackedbar-horizontal',
  styleUrl: 'fluid-stackedbar-horizontal.scss',
  shadow: true,
})
export class FluidStackedbarHorizontal
  implements FluidThemeInterface, FluidChartInterface, ComponentInterface
{
  // ================================================================ //
  // -- Own Properties (Private)

  /**
   * Intrinsic/extrinsic width of the chart.
   */
  private _elementWidth: number;

  /**
   * Reference to the <canvas> element that is used to render the chart.
   */
  private _chartRef: HTMLCanvasElement;

  /**
   * Reference to the Chart.js chart instance.
   */
  private _chart: Chart;

  // ================================================================ //
  // -- Host Element

  /**
   * Reference to the host element.
   */
  @Element() host: HTMLFluidStackedbarHorizontalElement;

  // ================================================================ //
  // -- Internationalisation

  /* @internal */
  @Prop({ mutable: true }) fluidTranslate: FluidStackedChartTranslationOptions;
  private _translateId: string = generateUniqueId();
  @State() private _i18nStore: FluidI18nStore | undefined;

  // ================================================================ //
  // -- States

  /**
   * Current font size of the Chart's legend items.
   */
  @State() _legendFontSize: number;

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * Make sure that the chart colors reflect the legend. For some reason,
   * the chart colors will set to LM and then the chart will switch to
   * legacy. This is a hacky solution to that issue, will update the
   * colors and re-render the chart when the legend is updated.
   */
  @Watch('theme')
  themeWatchHandler(newValue: string, oldValue: string) {
    if (newValue !== oldValue && this._chart && !this.colorOverride) {
      const { colors } = FluidChartStyles[this.theme];

      this._chart.data.datasets.forEach((set, index) => {
        set.backgroundColor = colors[index];
        set.datalabels.color = ChartUtils.pickTextColor(
          colors[index],
          WHITE,
          DARK_BLUE
        );
      });
      // Updates font sizes
      ChartUtils.updateChart(this._chart);
    }
  }

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * A unique identifier for your chart canvas element
   * Useful to specify for E2E, analytics, etc.
   * Defaults to a unique 36 char string
   */
  @Prop({ mutable: true }) chartId: string = generateUniqueId();

  /**
   * When provided, displays a heading title above the chart.
   */
  @Prop({ mutable: true }) chartTitle: string;

  /**
   * Supplementary subheading for the chart displayed below the "title".
   */
  @Prop({ mutable: true }) subTitle: string;

  /**
   * Takes an array of values for the bars in the chart
   * Each value is a number array, one per dataset
   */
  @Prop({ mutable: true }) chartData: number[][];

  @Watch('chartData')
  chartDataWatchHandler(newValue: number[][], oldValue: number[][]) {
    if (newValue !== oldValue) {
      let { colors } = FluidChartStyles[this.theme];

      if (this.colorOverride) {
        colors = ChartUtils.createColorsSequence(
          this.colorOverride,
          this.chartData as []
        );
      }

      ChartUtils.dataHandler(this._chart, undefined, newValue, colors);
    }
  }

  /**
   * Takes an array of string labels for the bars in the chart
   */
  @Prop({ mutable: true }) chartLabels: string[];

  @Watch('chartLabels')
  chartLabelsWatchHandler(newValue: string[], oldValue: string[]) {
    if (newValue !== oldValue) ChartUtils.labelHandler(this._chart, newValue);
  }

  /**
   * Takes in chart hover data to be rendered as a tooltip.
   * Each value is a object array, one per dataset
   */
  @Prop({ mutable: true }) chartHoverData: object[][];

  @Watch('chartHoverData')
  chartHoverDataWatchHandler(newValue: object[][], oldValue: object[][]) {
    if (newValue !== oldValue)
      this._chart.options.tooltips.callbacks.afterBody = (tooltipItems) =>
        Object.entries(
          this.chartHoverData[tooltipItems[0].datasetIndex][
            tooltipItems[0].index
          ]
        )
          .map(([key, val]) => `${key}: ${val}`)
          .join('\n');
  }

  /**
   * Allows the user to pass in a unit of currency to prepend to to chart values
   * Defaults to an empty string if undefined, should you wish to not have currency in your units
   */
  @Prop({ mutable: true }) currencySymbol = '';

  /**
   * When provided, adds a white background with subtle drop shadow
   * to the chart's container.
   */
  @Prop({ mutable: true }) hasBackground = false;

  /**
   * A label to describe the data units in the chart (required).
   */
  @Prop({ mutable: true }) label = 'No label provided';

  /**
   * Allows user to feed in a double array to allow for a legend of different types
   * Example:
   * [["Flood, Hail, EM", "Low", "Moderate", "High", "Severe"],["Wind", "None", "Tier 2", "Tier 1", "Tier 1 - Florida"]]
   * This will produce a 2 level legend with 2 sets of corresponding categories, from left to right
   * The first legendLabels[n][0] will always be the name for that category
   */
  @Prop({ mutable: true }) legendLabels: string[][];

  /**
   * Allows the user to set the position of the legends
   */
  @Prop({ mutable: true }) legendPosition: Exclude<
    FluidVerticalPosition,
    FluidVerticalPosition.MIDDLE
  > = FluidVerticalPosition.BOTTOM;

  @Watch('legendPosition')
  legendPositionChanged() {
    this.renderChart();
  }

  /**
   * Allows the user to decide the orientation of the legend
   */
  @Prop({ mutable: true }) legendOrientation: 'horizontal' | 'vertical' =
    'horizontal';

  @Watch('legendOrientation')
  legendOrientationChanged() {
    this.renderChart();
  }

  /**
   * Lets the user decide if they want a legend
   */
  @Prop({ mutable: true }) hasLegend = true;

  /**
   * Allows the user to pass in a custom color scheme for the stacked bars.
   * If prop is not given the color scheme will default to styles in FluidChartStyles
   * When this is set the theme colors will be ignored
   */
  @Prop({ mutable: true }) colorOverride: string[];

  /**
   * Percent (0-1) of the available width each bar should be within the category width.
   * 1.0 will take the whole category width and put the bars right next to each other
   */
  @Prop({ mutable: true }) barPercentage = 0.9;

  /**
   * Percent (0-1) of the available width each category should be within the sample width
   */
  @Prop({ mutable: true }) categoryPercentage = 0.8;

  /**
   * If true, the section label on the stacked bars will be removed
   */
  @Prop({ mutable: true }) removeSectionLabels: boolean;

  @Watch('removeSectionLabels')
  removePercentageLabelsChanged() {
    this.renderChart();
  }

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   */
  connectedCallback(): void {
    useI18n.bind(this)({
      language: () => this.setChartLabels(),
    });
    Utils.setGlobalTheme(this);
  }

  /**
   * Lifecycle Method to create the chart once the component loads
   *  data - takes in at least one dataset
   *  options - for styling and tweaking chart settings
   */
  componentDidLoad() {
    this.renderChart();
  }

  /**
   * Called every time the component is disconnected from the DOM.
   */
  disconnectedCallback() {
    this._i18nStore?.deregisterElement(this._translateId);
  }

  // ================================================================ //
  // -- Local Methods

  /**
   * Updates the chart labels whenever the language changes in the i18n provider.
   */
  setChartLabels() {
    this._chart.data.labels =
      this._i18nStore?.translate(this.fluidTranslate?.chartLabels) ||
      this.chartLabels;
    this._chart.update();
  }

  /**
   * Renders chart & sets internal properties based on chart config.
   */
  renderChart() {
    this._elementWidth = this.host.clientWidth;
    this._legendFontSize = ChartUtils.getChartBodyFontSize(this._elementWidth);
    let { colors } = FluidChartStyles[this.theme];
    const { fonts } = FluidChartStyles[this.theme];

    if (this.colorOverride) {
      colors = ChartUtils.createColorsSequence(
        this.colorOverride,
        this.chartData as []
      );
    }

    const myChartRef = this._chartRef.getContext('2d');
    const datasets = this.chartData
      ? ChartUtils.datasetInterfaceBuilder(this.chartData, colors).map(
          (item) => {
            item.barPercentage = this.barPercentage;
            item.categoryPercentage = this.categoryPercentage;
            return item;
          }
        )
      : undefined;

    this._chart = new Chart(myChartRef, {
      type: 'horizontalBar',
      data: {
        labels:
          this._i18nStore?.translate(this.fluidTranslate?.chartLabels) ||
          this.chartLabels,
        datasets: datasets,
      },
      options: {
        plugins: {
          // Controls the datalabels plugin
          datalabels: {
            // Will display a label if it can fit in a stacked bar
            // Sum could not be made a global variable so the function is preformed various times
            display: (ctx) => {
              if (this.removeSectionLabels) return false;
              const data = ctx.chart.data;
              const value: any = ctx.dataset.data[ctx.dataIndex];
              let sum: any = 0;
              for (let i = 0; i < data.datasets.length; i++) {
                sum += data.datasets[i].data[ctx.dataIndex];
              }
              const percentage = Math.round((value / sum) * 100);

              return percentage > 15;
            },
            opacity: () => (this.removeSectionLabels ? 0 : 1),
            font: (context) => ({
              size: ChartUtils.fontLabelScaler(context.chart.width),
              weight: 'bold',
              family: fonts,
            }),
            // Formats the label to a percentage
            formatter: (value, ctx) => {
              const data = ctx.chart.data;
              let sum: any = 0;
              for (let i = 0; i < data.datasets.length; i++) {
                sum += data.datasets[i].data[ctx.dataIndex];
              }
              return Math.round((value / sum) * 100) + '%';
            },
          },
        },
        layout: {
          padding: {
            left: 8,
            right: 8,
            top: 8,
            bottom: 8,
          },
        },
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: 4 / 3,
        scales: {
          xAxes: [
            {
              stacked: true,
              ticks: {
                beginAtZero: true,
                fontFamily: fonts,
                fontSize: ChartUtils.getChartBodyFontSize(this._elementWidth),
                callback: (value: number): string =>
                  ChartUtils.toTruncated(value, 0, this.currencySymbol),
              },
            },
          ],
          yAxes: [
            {
              stacked: true,
              gridLines: { display: false },
              ticks: {
                fontFamily: fonts,
                fontSize: ChartUtils.getChartBodyFontSize(this._elementWidth),
              },
            },
          ],
        },
        title: {
          display: false, // We are using a custom element
        },
        legend: {
          display: false, // Deemed unnecessary
        },
        tooltips: {
          titleFontFamily: fonts,
          titleFontSize: ChartUtils.getChartBodyFontSize(this._elementWidth),
          bodyFontFamily: fonts,
          bodyFontSize: ChartUtils.getChartBodyFontSize(this._elementWidth),
          mode: 'nearest',
          displayColors: false, // Deemed redundant and made the tooltip cramped
          callbacks: {
            title: () => {
              return null;
            },
            // Displays percentage on hover
            label: (tooltipItems, data) => {
              const value: any =
                data.datasets[tooltipItems.datasetIndex].data[
                  tooltipItems.index
                ];
              let sum: any = 0;
              for (let i = 0; i < data.datasets.length; i++) {
                sum += data.datasets[i].data[tooltipItems.index];
              }
              return Math.round((value / sum) * 100) + '%';
            },
            // Displays the data in terms of its specified units
            afterLabel: (tooltipItems, data) => {
              const value: any =
                data.datasets[tooltipItems.datasetIndex].data[
                  tooltipItems.index
                ];
              const unitLabel =
                this._i18nStore?.translate(this.fluidTranslate?.label) ||
                this.label;
              return `${unitLabel}: ${
                this.currencySymbol
              }${value.toLocaleString()}`;
            },
            // Modifies hover tooltip element with anything hoverData passes, designed to handle unlimited entries
            afterBody: (tooltipItems) => {
              if (this.chartHoverData === undefined) {
                return '';
              } else {
                const translatedHoverData = this._i18nStore?.translate(
                  this.fluidTranslate?.chartHoverData
                );
                const index =
                  translatedHoverData?.[0] ||
                  this.chartHoverData[tooltipItems[0].datasetIndex][
                    tooltipItems[0].index
                  ];
                return Object.entries(index)
                  .map(([key, val]) => `${key}: ${val}`)
                  .join('\n');
              }
            },
          },
        },
        // @ts-ignore - This is a bug in the typings, the function signature is correct
        onResize: (chartInstance: Chart, size: ChartSize): void => {
          if (isNullOrUndefined(chartInstance)) return;
          this._elementWidth = size.width;
          this._legendFontSize = ChartUtils.getChartBodyFontSize(
            this._elementWidth
          );
          ChartUtils.updateChart(chartInstance);
        },
      },
    });

    this._chart.render();
  }

  /**
   * Used for calculating relative sizes
   * @return the chart element
   */
  getChart() {
    return this._chart;
  }

  /**
   * Used for calculating relative sizes
   * @return The width of the containing element
   */
  getElementWidth(): number {
    return this._elementWidth;
  }

  /**
   * Creates a Legend Component, which will run if supplied with the correct data
   * Custom legend is included for readability, allows the user to quickly see the pie splice percentage
   */
  renderLegend(): HTMLElement {
    if (!this.hasLegend || !this._legendFontSize) return;

    let { colors } = FluidChartStyles[this.theme];
    if (this.colorOverride) {
      colors = ChartUtils.createColorsSequence(
        this.colorOverride,
        this.chartData as []
      );
    }

    // Render each element in the array as a grid component in a CSS grid div, with a sub-grid within each grid-item for the color div
    return (
      <div class={`legend-container-${this.legendOrientation}`}>
        {(
          this._i18nStore?.translate(this.fluidTranslate?.legendLabels) ||
          this.legendLabels
        ).map((element) => {
          return element.map((subElement, subIndex) => {
            const toRet = [];
            if (subIndex === 0) {
              toRet.push(
                <strong
                  class='legend-title'
                  title={subElement}
                  style={{
                    fontSize: `${this._legendFontSize}px`,
                  }}>
                  {subElement}
                </strong>
              );
            }

            if (subIndex !== 0) {
              const svgSize = this._legendFontSize + 4;
              toRet.push(
                <div class={`legend-label-${this.legendOrientation}`}>
                  <ChartLegendMarker
                    size={svgSize}
                    color={colors[subIndex - 1]}
                  />
                  <span
                    title={subElement}
                    style={{
                      fontSize: `${this._legendFontSize}px`,
                    }}>
                    {subElement}
                  </span>
                </div>
              );
            }
            return toRet;
          });
        })}
      </div>
    );
  }

  getLegends(display: boolean) {
    if (!display) return;

    return (
      <div class='legend'>
        {this.chartData !== undefined && this.legendLabels !== undefined ? (
          this.renderLegend()
        ) : (
          <h3>
            {this._i18nStore?.translate(this.fluidTranslate?.noLegend) ||
              'Legend prop not provided'}
          </h3>
        )}
      </div>
    );
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    return (
      <div
        class={{
          [`fluid-stackedbar-horizontal theme-${this.theme}`]: true,
          'has-background': this.hasBackground,
        }}>
        <div class='container'>
          <ChartTitle
            title={this.chartTitle}
            fluidTranslate={this.fluidTranslate}
            i18nStore={this._i18nStore}
            theme={this.theme}
          />
          <ChartSubtitle
            subTitle={this.subTitle}
            fluidTranslate={this.fluidTranslate}
            i18nStore={this._i18nStore}
            theme={this.theme}
          />
          {this.getLegends(
            this.hasLegend && this.legendPosition === FluidVerticalPosition.TOP
          )}
          <canvas
            id={this.chartId}
            ref={(el: HTMLCanvasElement) => (this._chartRef = el)}
          />
          {this.getLegends(
            this.hasLegend &&
              this.legendPosition === FluidVerticalPosition.BOTTOM
          )}
        </div>
      </div>
    );
  }
}
